import { sanitizeDeep } from './sanitize';
import { getShapeFillPaint, getShapeStrokePaint, getTextPaint, normalizePaint } from './paintUtils';
import { clampFrameContent, computeCoverScale, parseViewBox } from './frameUtils';

const clamp = (value, min, max, fallback) => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return fallback;
  }
  return Math.min(Math.max(Number(value), min), max);
};

const normalizeFontWeight = (value) => {
  const allowed = [400, 500, 600, 700];
  const numeric = Number(value);
  if (allowed.includes(numeric)) return numeric;
  return 400;
};

const ensureString = (value, fallback = '') =>
  typeof value === 'string' ? value : fallback;

export const normalizeElement = (element = {}) => {
  const inferredType =
    element.type === 'shape' && element.shapeType === 'frame'
      ? 'frame'
      : element.type || 'shape';
  const base = {
    id: ensureString(element.id),
    type: inferredType,
    x: clamp(element.x, -100000, 100000, 0),
    y: clamp(element.y, -100000, 100000, 0),
    w: clamp(element.w, 1, 100000, 100),
    h: clamp(element.h, 1, 100000, 100),
    rotation: clamp(element.rotation, -360, 360, 0),
    opacity: clamp(element.opacity, 0, 1, 1),
    locked: Boolean(element.locked),
    zIndex: clamp(element.zIndex, -10000, 10000, 1),
  };

  if (base.type === 'text') {
    const text = ensureString(element.text);
    const fontFamily = ensureString(element.fontFamily, 'Inter, sans-serif');
    const fontSize = clamp(element.fontSize, 6, 300, 48);
    const fontStyle = element.fontStyle === 'italic' ? 'italic' : 'normal';
    const fontWeight = normalizeFontWeight(element.fontWeight);
    const lineHeight = clamp(element.lineHeight, 0.8, 2.5, 1.2);
    const letterSpacing = clamp(element.letterSpacing, -2, 20, 0);
    const paddingX = clamp(element.paddingX, 0, 48, 0);
    const paddingY = clamp(element.paddingY, 0, 48, 0);
    const autoWidth = element.autoWidth === undefined ? true : Boolean(element.autoWidth);
    const autoHeight = element.autoHeight === undefined ? true : Boolean(element.autoHeight);
    const minW = clamp(element.minW, 20, 2000, 40);

    const paint = getTextPaint(element);
    const legacyColor = ensureString(element.color, paint.type === 'solid' ? paint.color : '#111111');
    return sanitizeDeep({
      ...base,
      text,
      fontFamily,
      fontSize,
      fontWeight,
      fontStyle,
      underline: Boolean(element.underline),
      color: legacyColor,
      align: ['left', 'center', 'right'].includes(element.align)
        ? element.align
        : 'left',
      lineHeight,
      letterSpacing,
      paddingX,
      paddingY,
      autoWidth,
      autoHeight,
      minW,
      style: {
        ...(element.style || {}),
        paint,
      },
    });
  }

  if (base.type === 'frame') {
    const frameSource = element.frame || {};
    const legacyFillPaint = getShapeFillPaint(element);
    const legacyStrokePaint = getShapeStrokePaint(element);
    const fallbackBackground =
      legacyFillPaint.type === 'solid' ? legacyFillPaint.color : '#F6F2E8';
    const fallbackBorder =
      legacyStrokePaint.type === 'solid' ? legacyStrokePaint.color : '#FFFFFF';
    const backgroundPaint = normalizePaint(
      element.backgroundPaint
        || frameSource.backgroundPaint
        || element.style?.fillPaint
        || element.fillPaint
        || element.fill,
      fallbackBackground,
    );

    const viewBox = ensureString(frameSource.viewBox, '0 0 100 100');
    const viewBoxMeta = parseViewBox(viewBox);
    const shapeType = ['path', 'rect', 'circle'].includes(frameSource.shapeType)
      ? frameSource.shapeType
      : frameSource.pathD
        ? 'path'
        : 'rect';
    const cornerRadius = clamp(
      frameSource.cornerRadius ?? element.radius,
      0,
      400,
      shapeType === 'circle' ? Math.min(base.w, base.h) / 2 : 12,
    );

    const legacyBorderEnabled =
      element.strokeEnabled !== false && clamp(element.strokeWidth, 0, 200, 0) > 0;
    const borderSource = frameSource.border || {};
    const shadowSource = frameSource.shadow || {};

    const contentSource =
      element.content && element.content.type === 'image' ? element.content : null;
    let content = null;
    if (contentSource) {
      const naturalW = clamp(contentSource.naturalW, 1, 100000, base.w);
      const naturalH = clamp(contentSource.naturalH, 1, 100000, base.h);
      const coverScale = computeCoverScale(base.w, base.h, naturalW, naturalH);
      const scale = clamp(contentSource.scale, coverScale, coverScale * 10, coverScale);
      content = clampFrameContent(base, {
        ...contentSource,
        type: 'image',
        assetId: ensureString(contentSource.assetId),
        src: ensureString(contentSource.src),
        naturalW,
        naturalH,
        scale,
        offsetX: contentSource.offsetX,
        offsetY: contentSource.offsetY,
      });
    }

    return sanitizeDeep({
      ...base,
      fillEnabled: element.fillEnabled !== false,
      backgroundPaint,
      frame: {
        frameId: ensureString(frameSource.frameId, 'frame-rect-01'),
        shapeType,
        viewBox,
        viewBoxWidth: clamp(frameSource.viewBoxWidth, 1, 10000, viewBoxMeta.width),
        viewBoxHeight: clamp(frameSource.viewBoxHeight, 1, 10000, viewBoxMeta.height),
        pathD: ensureString(frameSource.pathD),
        cornerRadius,
        border: {
          enabled: borderSource.enabled === undefined
            ? legacyBorderEnabled
            : Boolean(borderSource.enabled),
          color: ensureString(borderSource.color, fallbackBorder),
          width: clamp(borderSource.width, 0, 200, legacyBorderEnabled ? clamp(element.strokeWidth, 0, 200, 2) : 0),
        },
        shadow: {
          enabled: Boolean(shadowSource.enabled),
          blur: clamp(shadowSource.blur, 0, 200, 0),
          opacity: clamp(shadowSource.opacity, 0, 1, 0),
          x: clamp(shadowSource.x, -400, 400, 0),
          y: clamp(shadowSource.y, -400, 400, 0),
        },
      },
      content,
    });
  }

  if (base.type === 'image') {
    const crop = element.crop || {};
    const filters = element.filters || {};
    const adjustSource = element.adjust || {};
    const adjustWarmth = adjustSource.warmth !== undefined
      ? adjustSource.warmth
      : filters.warmth !== undefined
        ? filters.warmth * 100
        : 0;
    const adjust = {
      brightness: adjustSource.brightness ?? filters.brightness ?? 1,
      contrast: adjustSource.contrast ?? filters.contrast ?? 1,
      saturation: adjustSource.saturation ?? filters.saturation ?? 1,
      warmth: adjustWarmth,
      blur: adjustSource.blur ?? filters.blur ?? 0,
      temperature: adjustSource.temperature ?? 0,
      tint: adjustSource.tint ?? 0,
      highlights: adjustSource.highlights ?? 0,
      shadows: adjustSource.shadows ?? 0,
      whites: adjustSource.whites ?? 0,
      blacks: adjustSource.blacks ?? 0,
      vibrance: adjustSource.vibrance ?? 0,
      invert: Boolean(adjustSource.invert),
      sharpness: adjustSource.sharpness ?? 0,
      clarity: adjustSource.clarity ?? 0,
      vignette: adjustSource.vignette ?? 0,
    };
    const shadow = element.shadow || {};
    const bgRemoval = element.bgRemoval || {};
    const eraser = element.eraser || {};
    const eraserStrokes = Array.isArray(eraser.strokes) ? eraser.strokes : [];
    const magic = eraser.magic || {};
    const magicOps = Array.isArray(magic.ops) ? magic.ops : [];
    const hasMagicOps = magicOps.length > 0;
    const hasPixelOps = eraserStrokes.length > 0;
    const eraserEnabled = eraser.enabled === undefined
      ? hasMagicOps || hasPixelOps
      : Boolean(eraser.enabled);

    return sanitizeDeep({
      ...base,
      src: ensureString(element.src),
      flipX: Boolean(element.flipX),
      flipY: Boolean(element.flipY),
      radius: clamp(element.radius, 0, 120, 0),
      crop: {
        enabled: Boolean(crop.enabled),
        x: clamp(crop.x, 0, 1, 0),
        y: clamp(crop.y, 0, 1, 0),
        w: clamp(crop.w, 0, 1, 1),
        h: clamp(crop.h, 0, 1, 1),
        mode: ['free', 'original', '1:1', '4:5', '16:9'].includes(crop.mode)
          ? crop.mode
          : 'free',
      },
      filters: {
        brightness: clamp(filters.brightness, 0, 2, 1),
        contrast: clamp(filters.contrast, 0, 2, 1),
        saturation: clamp(filters.saturation, 0, 2, 1),
        warmth: clamp(filters.warmth, -1, 1, 0),
        blur: clamp(filters.blur, 0, 20, 0),
      },
      adjust: {
        brightness: clamp(adjust.brightness, 0, 2, 1),
        contrast: clamp(adjust.contrast, 0, 2, 1),
        saturation: clamp(adjust.saturation, 0, 2, 1),
        warmth: clamp(adjust.warmth, -100, 100, 0),
        blur: clamp(adjust.blur, 0, 20, 0),
        temperature: clamp(adjust.temperature, -100, 100, 0),
        tint: clamp(adjust.tint, -100, 100, 0),
        highlights: clamp(adjust.highlights, -100, 100, 0),
        shadows: clamp(adjust.shadows, -100, 100, 0),
        whites: clamp(adjust.whites, -100, 100, 0),
        blacks: clamp(adjust.blacks, -100, 100, 0),
        vibrance: clamp(adjust.vibrance, -100, 100, 0),
        invert: Boolean(adjust.invert),
        sharpness: clamp(adjust.sharpness, 0, 100, 0),
        clarity: clamp(adjust.clarity, -100, 100, 0),
        vignette: clamp(adjust.vignette, -100, 100, 0),
      },
      filterPreset: [
        'none',
        'fresco',
        'belvedere',
        'noir',
        'vivid',
        'warm',
        'cool',
      ].includes(element.filterPreset)
        ? element.filterPreset
        : 'none',
      shadow: {
        enabled: Boolean(shadow.enabled),
        x: clamp(shadow.x, -50, 50, 0),
        y: clamp(shadow.y, -50, 50, 0),
        blur: clamp(shadow.blur, 0, 80, 20),
        color: ensureString(shadow.color, '#000000'),
        opacity: clamp(shadow.opacity, 0, 1, 0.2),
      },
      bgRemoval: {
        status: ['none', 'processing', 'done', 'failed'].includes(bgRemoval.status)
          ? bgRemoval.status
          : 'none',
        processedSrc: ensureString(bgRemoval.processedSrc, ''),
        maskUrl: ensureString(bgRemoval.maskUrl, ''),
        error: ensureString(bgRemoval.error, ''),
      },
      eraser: {
        enabled: eraserEnabled,
        mode: ['pixel', 'magic'].includes(eraser.mode) ? eraser.mode : 'pixel',
        brushSize: clamp(eraser.brushSize, 4, 200, 24),
        hardness: clamp(eraser.hardness, 0, 1, 0.8),
        opacity: clamp(eraser.opacity, 0, 1, 1),
        strokes: eraserStrokes.map((stroke) => ({
          id: ensureString(stroke.id, `stroke-${Math.random().toString(36).slice(2, 9)}`),
          points: Array.isArray(stroke.points)
            ? stroke.points.map((pt) => ({
              x: clamp(pt.x, 0, 1, 0),
              y: clamp(pt.y, 0, 1, 0),
            }))
            : [],
          size: clamp(stroke.size, 1, 400, clamp(eraser.brushSize, 4, 200, 24)),
          hardness: clamp(stroke.hardness, 0, 1, clamp(eraser.hardness, 0, 1, 0.8)),
          opacity: clamp(stroke.opacity, 0, 1, clamp(eraser.opacity, 0, 1, 1)),
          composite: stroke.composite === 'destination-out' ? 'destination-out' : 'destination-out',
        })),
        magic: {
          enabled: Boolean(magic.enabled),
          tolerance: clamp(magic.tolerance, 0, 100, 25),
          contiguous: magic.contiguous === undefined ? true : Boolean(magic.contiguous),
          feather: clamp(magic.feather, 0, 20, 2),
          expand: clamp(magic.expand, -10, 10, 0),
          opMode: magic.opMode === 'subtract' ? 'subtract' : 'add',
          ops: magicOps.map((op) => ({
            id: ensureString(op.id, `magic-${Math.random().toString(36).slice(2, 9)}`),
            x: clamp(op.x, 0, 1, 0.5),
            y: clamp(op.y, 0, 1, 0.5),
            tolerance: clamp(op.tolerance, 0, 100, clamp(magic.tolerance, 0, 100, 25)),
            contiguous: op.contiguous === undefined
              ? magic.contiguous === undefined
                ? true
                : Boolean(magic.contiguous)
              : Boolean(op.contiguous),
            feather: clamp(op.feather, 0, 20, clamp(magic.feather, 0, 20, 2)),
            expand: clamp(op.expand, -10, 10, clamp(magic.expand, -10, 10, 0)),
            opMode: op.opMode === 'subtract' ? 'subtract' : 'add',
            createdAt: op.createdAt || Date.now(),
          })),
        },
      },
    });
  }

  if (base.type === 'shape') {
    const fillPaint = getShapeFillPaint(element);
    const strokePaint = getShapeStrokePaint(element);
    const fallbackFill = fillPaint.type === 'solid' ? fillPaint.color : '#e1dccf';
    const fallbackStroke = strokePaint.type === 'solid' ? strokePaint.color : '#c9a962';
    return sanitizeDeep({
      ...base,
      shapeType: [
        'rect',
        'circle',
        'line',
        'arrow',
        'triangle',
        'diamond',
        'star',
        'speech',
        'badge',
        'blob',
        'graphic',
      ].includes(element.shapeType)
        ? element.shapeType
        : 'rect',
      svg: ensureString(element.svg),
      fill: ensureString(element.fill, fallbackFill),
      stroke: ensureString(element.stroke, fallbackStroke),
      strokeWidth: clamp(element.strokeWidth, 0, 50, 2),
      strokeStyle: ['solid', 'dashed', 'dotted'].includes(element.strokeStyle)
        ? element.strokeStyle
        : 'solid',
      lineCap: ['round', 'square', 'butt'].includes(element.lineCap)
        ? element.lineCap
        : 'round',
      arrowHead: ['none', 'start', 'end', 'both'].includes(element.arrowHead)
        ? element.arrowHead
        : 'none',
      arrowHeadSize: clamp(element.arrowHeadSize, 6, 60, 16),
      radius: clamp(element.radius, 0, 200, 0),
      flipX: Boolean(element.flipX),
      flipY: Boolean(element.flipY),
      fillEnabled: element.fillEnabled === undefined
        ? !['line', 'arrow'].includes(element.shapeType)
        : Boolean(element.fillEnabled),
      strokeEnabled: element.strokeEnabled === undefined ? true : Boolean(element.strokeEnabled),
      style: {
        ...(element.style || {}),
        fillPaint: normalizePaint(element.style?.fillPaint || element.fillPaint, fallbackFill),
        strokePaint: normalizePaint(element.style?.strokePaint || element.strokePaint, fallbackStroke),
      },
    });
  }

  return sanitizeDeep(base);
};

export const normalizeElements = (elements = []) =>
  elements.map((el) => normalizeElement(el));

export const normalizeImageElement = (element = {}) =>
  normalizeElement({ ...element, type: 'image' });
